import { z } from "zod"

export const STORE_GRADES = ["A", "B", "C", "D"] as const
export type StoreGrade = (typeof STORE_GRADES)[number]

/** Outlet is a format, never a grade. */
export const STORE_FORMATS = [
  { value: "flagship", label: "Flagship" },
  { value: "outlet", label: "Outlet" },
  { value: "shopInShop", label: "Shop-in-shop" },
  { value: "partner", label: "Partner" },
  { value: "popup", label: "Pop-up" },
] as const
export type StoreFormat = (typeof STORE_FORMATS)[number]["value"]
export const STORE_FORMAT_VALUES = STORE_FORMATS.map((format) => format.value) as [
  StoreFormat,
  ...StoreFormat[],
]
export const OUTLET_FORMAT: StoreFormat = "outlet"

export function storeFormatLabel(value: string): string {
  return STORE_FORMATS.find((format) => format.value === value)?.label ?? value
}

export const STORE_SIZES = ["Small", "Medium", "Large"] as const

export const MAX_TRANSIT_DAYS = 365
export const MAX_SELLING_AREA = 100_000
export const MAX_DELIVERY_ADDRESS_LENGTH = 500
export const MAX_STORE_SIZE_LENGTH = 40

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === "" ? null : value))

/** One store's staged edits; a field left out keeps its saved value. */
export const storePatchSchema = z.object({
  storeId: z.string(),
  grade: z.enum(STORE_GRADES).nullable().optional(),
  format: z.enum(STORE_FORMAT_VALUES).optional(),
  storeSize: nullableText(MAX_STORE_SIZE_LENGTH).optional(),
  sellingArea: z.number().int().min(0).max(MAX_SELLING_AREA).nullable().optional(),
  transitDays: z.number().int().min(0).max(MAX_TRANSIT_DAYS).optional(),
  deliveryAddress: nullableText(MAX_DELIVERY_ADDRESS_LENGTH).optional(),
})
export type StorePatch = z.input<typeof storePatchSchema>
export type StoreField = Exclude<keyof StorePatch, "storeId">

export const MAX_STORE_PATCHES = 1_000

/** Labels from the allocation term dictionary. */
export const STORE_FIELD_LABELS: Record<StoreField, string> = {
  grade: "Store grade",
  format: "Store format",
  storeSize: "Store size",
  sellingArea: "Selling area (m2)",
  transitDays: "Transit time (days)",
  deliveryAddress: "Delivery address (ship-to)",
}
